import { useEffect, useState } from 'react';
import { api, type PainelIa } from '../api.js';

/**
 * IA na visão de admin: chave, crédito e consumo por cliente.
 *
 * Três coisas que só existem aqui porque a Anthropic não as oferece:
 *
 *  - **Trocar a chave sem deploy.** Em variável de ambiente ela exige acesso ao
 *    Azure e um restart. No dia em que o pipeline caiu isso ficou claro.
 *  - **Saldo.** Não há API pública de saldo. O que aparece é a NOSSA conta:
 *    comprado menos medido. A tela diz isso com todas as letras, porque
 *    estimativa apresentada como extrato é o tipo de número que faz alguém
 *    descobrir que o crédito acabou quando o texto para de sair.
 *  - **Consumo por cliente.** A conta na Anthropic é uma só; o console reporta
 *    o total da organização. O recorte por cliente vem de medirmos cada chamada.
 */

const AVISO: Record<PainelIa['credito']['alerta'], { tom: string; texto: string } | null> = {
  ok: null,
  sem_credito: {
    tom: 'warn',
    texto:
      'Nenhuma compra de crédito registrada. Sem isso não há como avisar quando ' +
      'renovar — informe abaixo quanto você colocou e por quantos dias deve durar.',
  },
  saldo: {
    tom: 'err',
    texto:
      'Crédito estimado abaixo de 20% do que foi comprado. Quando acabar, a geração ' +
      'de texto para de funcionar (o resto do app continua). Hora de recarregar.',
  },
  prazo: {
    tom: 'warn',
    texto:
      'O prazo que você definiu para este crédito está terminando. Confira o saldo ' +
      'real no console da Anthropic e recarregue se precisar.',
  },
};

function usd(v: number): string {
  if (v === 0) return 'US$ 0';
  if (Math.abs(v) < 0.01) return `US$ ${v.toFixed(4)}`;
  return `US$ ${v.toFixed(2)}`;
}

export function AdminAi() {
  const [dados, setDados] = useState<PainelIa | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  const [novaChave, setNovaChave] = useState('');
  const [valor, setValor] = useState('');
  const [dias, setDias] = useState('30');

  useEffect(() => {
    void api
      .adminAi()
      .then(setDados)
      .catch((c: unknown) => setErro(c instanceof Error ? c.message : 'Falha ao carregar'));
  }, []);

  /** Toda ação devolve o painel inteiro, então a tela nunca fica defasada. */
  async function acao(fn: () => Promise<PainelIa>, sucesso: string) {
    setSalvando(true);
    setErro(null);
    setOk(null);
    try {
      setDados(await fn());
      setOk(sucesso);
    } catch (cause) {
      setErro(cause instanceof Error ? cause.message : 'A ação falhou');
    } finally {
      setSalvando(false);
    }
  }

  if (erro && !dados) return <div className="notice err">{erro}</div>;
  if (!dados) {
    return (
      <div className="card empty">
        <span className="spinner" /> Carregando…
      </div>
    );
  }

  const { chave, credito, consumo, modelo, modelosDisponiveis } = dados;
  const aviso = AVISO[credito.alerta];
  const gastoMedioDia =
    credito.compradoEm != null
      ? credito.gastoDesdeUsd /
        Math.max(1, (Date.now() - new Date(credito.compradoEm).getTime()) / 86_400_000)
      : null;

  return (
    <section style={{ display: 'grid', gap: 14 }}>
      {aviso && <div className={`notice ${aviso.tom}`}>{aviso.texto}</div>}
      {ok && <div className="notice ok">{ok}</div>}
      {erro && <div className="notice err">{erro}</div>}

      {/* ------------------------------------------------------------ crédito */}
      <div className="card" style={{ padding: 16 }}>
        <p className="section-title">Crédito da Anthropic</p>

        <div className="readout" style={{ marginBottom: 12 }}>
          <span>
            <strong>{credito.valorUsd != null ? usd(credito.valorUsd) : '—'}</strong>
            <span className="muted"> comprado</span>
          </span>
          <span>
            <strong>{usd(credito.gastoDesdeUsd)}</strong>
            <span className="muted"> gasto medido</span>
          </span>
          <span>
            <strong className={credito.alerta === 'saldo' ? 'err' : undefined}>
              {credito.saldoEstimadoUsd != null ? usd(credito.saldoEstimadoUsd) : '—'}
            </strong>
            <span className="muted"> sobra estimada</span>
          </span>
          <span>
            <strong>{credito.diasRestantes ?? '—'}</strong>
            <span className="muted"> dias até renovar</span>
          </span>
        </div>

        <p className="muted" style={{ fontSize: 12, margin: '0 0 12px' }}>
          A sobra é <strong>nossa estimativa</strong> (comprado − medido), não o extrato
          da Anthropic — não existe API pública de saldo. O número real está em
          console.anthropic.com › Billing.
          {credito.chamadasSemPreco > 0 && (
            <>
              {' '}
              {credito.chamadasSemPreco} chamada(s) usaram modelo sem preço cadastrado e
              não entram nesta conta.
            </>
          )}
          {gastoMedioDia != null && gastoMedioDia > 0 && (
            <>
              {' '}
              Ritmo atual: {usd(gastoMedioDia)}/dia.
            </>
          )}
        </p>

        <div className="row" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '0 0 150px' }}>
            <label htmlFor="cr-valor">Quanto colocou (US$)</label>
            <input
              id="cr-valor"
              type="number"
              min="1"
              step="1"
              value={valor}
              onChange={(e) => setValor(e.target.value)}
              placeholder="5"
            />
          </div>
          <div style={{ flex: '0 0 130px' }}>
            <label htmlFor="cr-dias">Deve durar (dias)</label>
            <input
              id="cr-dias"
              type="number"
              min="1"
              max="365"
              value={dias}
              onChange={(e) => setDias(e.target.value)}
            />
          </div>
          <button
            type="button"
            disabled={salvando || !valor}
            onClick={() =>
              void acao(
                () => api.adminSetAiCredit(Number(valor), Number(dias) || 30),
                'Crédito registrado. O aviso de renovação passa a contar daqui.',
              )
            }
          >
            {salvando ? <span className="spinner" /> : 'Registrar compra'}
          </button>
        </div>
      </div>

      {/* --------------------------------------------------------------- chave */}
      <div className="card" style={{ padding: 16 }}>
        <p className="section-title">Chave e modelo</p>

        <p style={{ margin: '0 0 12px', fontSize: 13 }}>
          {chave.configurada ? (
            <>
              <span className="badge ok">ativa</span>{' '}
              {chave.fonte === 'banco' ? (
                <>
                  cadastrada no painel
                  {chave.hint && (
                    <>
                      , terminando em <span className="mono">…{chave.hint}</span>
                    </>
                  )}
                  {chave.desde && (
                    <span className="muted">
                      {' '}
                      · desde {new Date(chave.desde).toLocaleDateString('pt-BR')}
                    </span>
                  )}
                </>
              ) : (
                <>
                  vindo da variável de ambiente do servidor
                  <span className="muted">
                    {' '}
                    · cadastre aqui para poder trocar sem deploy
                  </span>
                </>
              )}
            </>
          ) : (
            <>
              <span className="badge err">ausente</span> a geração de texto está usando
              exemplos.
            </>
          )}
        </p>

        <div className="row" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <label htmlFor="ai-key">Nova chave</label>
            <input
              id="ai-key"
              type="password"
              value={novaChave}
              onChange={(e) => setNovaChave(e.target.value)}
              placeholder="sk-ant-…"
              /* new-password para o Chrome não oferecer o e-mail de login aqui. */
              autoComplete="new-password"
            />
          </div>
          <button
            type="button"
            className="primary"
            disabled={salvando || novaChave.trim().length < 10}
            onClick={() =>
              void acao(async () => {
                const r = await api.adminSetAiKey(novaChave.trim());
                setNovaChave('');
                return r;
              }, 'Chave trocada. Já vale para a próxima geração de texto, sem deploy.')
            }
          >
            Trocar chave
          </button>
          {chave.fonte === 'banco' && (
            <button
              type="button"
              disabled={salvando}
              onClick={() =>
                void acao(
                  () => api.adminClearAiKey(),
                  'Chave removida do banco. Voltou a usar a do ambiente.',
                )
              }
              title="Remove a chave do banco e volta a usar a variável de ambiente"
            >
              Usar a do ambiente
            </button>
          )}
        </div>

        <div style={{ marginTop: 12, maxWidth: 300 }}>
          <label htmlFor="ai-model">Modelo</label>
          <select
            id="ai-model"
            value={modelo}
            disabled={salvando}
            onChange={(e) =>
              void acao(
                () => api.adminSetAiModel(e.target.value),
                'Modelo trocado. Vale para a próxima geração.',
              )
            }
          >
            {modelosDisponiveis.map((m) => (
              <option key={m} value={m}>
                {m}
                {m === 'claude-haiku-4-5' ? ' (mais barato)' : ''}
              </option>
            ))}
          </select>
          <p className="muted" style={{ fontSize: 12, margin: '5px 0 0' }}>
            Legenda de achadinho não precisa de modelo caro. Haiku custa 1/5 do Opus com
            qualidade equivalente neste uso.
          </p>
        </div>
      </div>

      {/* ------------------------------------------------------------ consumo */}
      <div className="card" style={{ padding: 16 }}>
        <p className="section-title">
          Consumo por cliente{' '}
          <span className="muted" style={{ fontWeight: 400 }}>
            {credito.compradoEm
              ? `(desde a compra, ${new Date(credito.compradoEm).toLocaleDateString('pt-BR')})`
              : '(todo o período)'}
          </span>
        </p>

        {consumo.porCliente.length === 0 ? (
          <p className="muted" style={{ margin: 0, fontSize: 12.5 }}>
            Nenhuma geração de texto medida ainda. Cada chamada passa a aparecer aqui,
            com o custo calculado sobre os tokens que a própria Anthropic reporta.
          </p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="offer-table">
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Cliente</th>
                  <th style={{ width: 90 }}>Textos</th>
                  <th style={{ width: 110 }}>Custo</th>
                  <th style={{ width: 120 }}>Tokens entrada</th>
                  <th style={{ width: 110 }}>Tokens saída</th>
                </tr>
              </thead>
              <tbody>
                {consumo.porCliente.map((c) => (
                  <tr key={c.accountId}>
                    <td>
                      <strong>{c.nome}</strong>
                    </td>
                    <td className="num">{c.chamadas.toLocaleString('pt-BR')}</td>
                    <td className="num">{usd(c.custoUsd)}</td>
                    <td className="num">{c.tokensEntrada.toLocaleString('pt-BR')}</td>
                    <td className="num">{c.tokensSaida.toLocaleString('pt-BR')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {consumo.ultimos7Dias.length > 0 && (
          <p className="muted" style={{ fontSize: 12, margin: '10px 0 0' }}>
            Últimos 7 dias:{' '}
            {consumo.ultimos7Dias
              .map(
                (d) =>
                  `${d.dia.slice(8)}/${d.dia.slice(5, 7)} ${usd(d.custoUsd)} (${d.chamadas})`,
              )
              .join(' · ')}
          </p>
        )}
      </div>
    </section>
  );
}
